import * as React from 'react';
import { Badge, Tooltip } from 'antd';
import * as moment from 'moment';
import { resolve } from 'inversify-react';
import { observer } from 'mobx-react';
import { observable, toJS } from 'mobx';

import { DataLocalService } from 'src/core/services/data-local.service';
import { ToolbarVisibility, DataTableView, Column } from 'src/core/ui/data-table.view';
import { SearchResultItem } from 'src/app/bookings/search/results/search-result-item';
import { FormatService } from 'src/core/services/format.service';
import { SearchResultTourItineraryConfig } from './search-result-tour-itinerary-config.view';
import { TourInfoCard } from '../../quote-entries/parts/tour-info-card';
import { ObjectUtils } from 'src/core/utils/object';

interface Props {
	search: Search;
	marketId: number;
	bookingDate: Date;
	languageCode: string;
	items: TourSearchResult[];
	setBusy: (isBusy: boolean) => void;
	selection: { [unitIndex: string]: ResultSelection };
	onChangeSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	onChangeOptionalSelection: (selectedRowKeys: string[] | number[], selectedRows: Object[]) => any;
	disableAddButton?: (val: boolean) => void;
}

interface State {
	showTourItineraryConfig: boolean;
}

@observer
export class TourCard extends React.Component<Props, State>{
	public state: State = { showTourItineraryConfig: false };

	@resolve
	private formatService: FormatService;

	@observable
	private selectedProducts: { [id: string]: ProductSearchResult[] } = {};

	public componentDidMount() {
		for (const item of this.props.items) {
			this.selectedProducts[item.id] = item.subProducts.filter(e => e.isRequired);
		}
	}

	public render() {
		const { items, setBusy, selection, search, bookingDate, languageCode, marketId, children, disableAddButton } = this.props;
		const { showTourItineraryConfig } = this.state;

		if (showTourItineraryConfig && !!selection) {
			return (
				<SearchResultTourItineraryConfig
					selection={selection[0]}
					cancelConfig={this.cancelConfig}
					search={search}
					setBusy={setBusy}
					bookingDate={bookingDate}
					languageCode={languageCode}
					marketId={marketId}
					disableAddButton={disableAddButton}
				>
					{children}
				</SearchResultTourItineraryConfig>
			);
		} else {
			const [item] = items;
			if (item.productType !== 9) {
				return null;
			}
			const model: any = new DataLocalService(items);

			let minPrice: number;
			let minPriceCurrency: string;
			let minPriceProduct: string;
			for (const unit of Object.getOwnPropertyNames(item.sellPrice)) {
				const unitIndex = parseInt(unit);
				const price = item.sellPrice[unitIndex];
				if (minPrice === undefined || minPrice > price.totalPrice) {
					minPrice = price.totalPrice;
					minPriceProduct = `${item.product.name}`;
					minPriceCurrency = price.currencyIsoCode;
				}
			}
			const extra = (
				<Badge count={items.length > 1 ? items.length : 0} style={{ right: '-15px', marginTop: '5px' }}>
					<Tooltip title={minPriceProduct}>
						{minPrice} {minPriceCurrency}
					</Tooltip>
				</Badge>
			);

			const selectionKeys = [];

			if (selection != null) {
				for (const prop of Object.getOwnPropertyNames(selection)) {
					const unitIndex = parseInt(prop);
					const id = selection[unitIndex].selected.id;
					if ([item].some(p => p.id === id)) {
						selectionKeys.push(`${id}`);
					}
				}
			}

			return (
				<SearchResultItem
					title={item.product.name}
					product={item.product}
					extra={extra}
				>
					<DataTableView
						rowKey={this.getKey}
						mode="display"
						setBusy={setBusy}
						hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
						columns={this.getColumns()}
						model={model}
						selection={{
							selectedRowKeys: selectionKeys,
							onChange: this.onSelectTours
						}}
					/>
				</SearchResultItem>
			);
		}
	}

	private getSubProcKey = (record: ProductSearchResult) => `${'tour'}-${record.id}`;

	private getKey = (record: ProductSearchResult) => `${record.id}`;

	private onSubProductSelect = (record: ProductSearchResult) => (selectedRowKeys: string[], selectedRows: any[]) => {
		this.selectedProducts[record.id] = [...toJS(this.selectedProducts[record.id]).filter(e => selectedRowKeys.indexOf(this.getSubProcKey(e)) === -1), ...selectedRows];
	}

	private selectAll = (record: ProductSearchResult) => (selected: boolean, selectedRows: any[], changeRows: Object[]) => {
		this.selectedProducts[record.id] = [...selectedRows];
	}

	private getSubproductCheckProps = (product: ProductSearchResult) => (record: ProductSearchResult & { parent?: ProductSearchResult }) => {
		const isDisabled = record.isRequired || (record.parent && record.parent.isRequired);
		let isChecked = record.isRequired;
		const parent = this.selectedProducts[product.id]
			? this.selectedProducts[product.id].find(s => s.id == record.parent.id)
			: null;
		if (!!parent) {
			isChecked = isChecked || !!parent.subProducts.find(s => s.id == record.id);
		}
		return {
			checked: isChecked,
			disabled: isDisabled
		}
	}

	private getCheckProps = (tour: TourSearchResult) => (record: ProductSearchResult) => {
		const isDisabled = record.isRequired;
		const isChecked = record.isRequired
			|| (this.selectedProducts[tour.id] != null
				? this.selectedProducts[tour.id].find(s => s.id == record.id) != null
				: false);

		return {
			checked: isChecked,
			disabled: isDisabled
		}
	}

	private onSelectTours = (selectedRowKeys: string[] | number[], selectedRows: ProductSearchResult[]) => {
		const selected: TourSearchResult = ObjectUtils.deepCopy(selectedRows[0]);

		if (this.selectedProducts[selected.id] && this.selectedProducts[selected.id].length > 0) {
			let totalCost: Money = null
			let totalSell: Money = null;
			selected.subProducts = this.selectedProducts[selected.id];
			let unit = 1;
			if (selected.features && selected.features.isPackage) {
				totalCost = { ...selected.costPrice[unit] };
				totalSell = { ...selected.sellPrice[unit] };
			} else if (selected.subProducts.length > 0) {
				let item = selected.subProducts[0];
				let props = Object.getOwnPropertyNames(toJS(item.sellPrice));
				if (props.length === 0 && item.subProducts.length === 1) {
					item = item.subProducts[0];
					props = Object.getOwnPropertyNames(toJS(item.sellPrice));
				}
				for (var prop of props) {
					unit = parseInt(prop);
					break;
				}
				totalCost = { ...item.costPrice[unit] };
				totalSell = { ...item.sellPrice[unit] }

				for (var prod of selected.subProducts.slice(1)) {
					if (!!prod.costPrice[unit]) {
						totalCost.totalPrice += prod.costPrice[unit].totalPrice;
					}
				}

				for (var prod of selected.subProducts.slice(1)) {
					if (!!prod.sellPrice[unit]) {
						totalSell.totalPrice += prod.sellPrice[unit].totalPrice;
					}
				}
			}

			const option: ResultOption = {
				cost: totalCost, // TODO calculate tour cost price
				sell: totalSell,
				id: selected.id,
				unitIndex: 0,
				result: selected,
			};

			this.props.onChangeSelection(selectedRowKeys, [option] as any);
			this.setState({ showTourItineraryConfig: true });
		}
	}

	private expandedRenderer = (product: ProductSearchResult) => {
		const cols = [
			{
				key: 'name',
				dataIndex: 'product.name',
				title: 'Name'
			}
		];

		const subProducts = product.subProducts.map(i => ({ ...i, parent: product }));
		const dataSet = new DataLocalService(subProducts as any);

		return (
			<DataTableView
				columns={cols}
				model={dataSet}
				rowKey={'id'}
				mode="display"
				setBusy={this.props.setBusy}
				hideToolbar={ToolbarVisibility.hideSearch | ToolbarVisibility.hideModifyButtons | ToolbarVisibility.hidePagination}
				selection={{
					getCheckboxProps: this.getSubproductCheckProps(product)
				}}
			/>
		);
	}

	private getColumns = (): Column[] => {
		const { selection } = this.props;

		return [
			{
				key: 'source',
				dataIndex: 'productProviderName',
				title: 'Source'
			},
			{
				key: 'name',
				//dataIndex: 'product.name',
				dataIndex: 'features.itineraryName',
				title: 'Name',
				//render: (text, record: TourSearchResult) => `${text} (${record.features.itineraryName})`,
				render: (text, record: TourSearchResult) => {
					if (record.features.tourItineraryDateId) {
						return `${text} (In Group)`;
					}
					return text;
				},
			},
			{
				key: 'supplier',
				dataIndex: 'supplier.title',
				title: 'Supplier',
			},
			{
				key: 'duration',
				dataIndex: 'endDate',
				title: 'Duration (days)',
				render: (text, record: ProductSearchResult) => moment.utc(record.endDate).diff(moment.utc(record.startDate), 'd'),
			},
			{
				key: 'returnDate',
				dataIndex: 'endDate',
				title: 'Return date',
				render: (text, record: ProductSearchResult) => this.formatService.toString('small-date', record.endDate),
			},
			{
				key: 'price',
				dataIndex: 'sellPrice',
				title: 'Price',
				render: this.priceRenderer,
			},
			{
				key: 'itinerary',
				dataIndex: 'subProducts',
				title: 'Itinerary',
				render: (result: TourProductSearchResult[], record: TourSearchResult) => {
					const columns: Column[] = [
						{
							key: 'product',
							dataIndex: '',
							title: 'Product',
							render: (text: any, record: ProductSearchResult) => {
								return record.product.name;
							}
						},
						{
							key: 'from',
							dataIndex: '',
							title: 'Date from',
							render: (text: any, record: ProductSearchResult) => {
								return this.formatService.toString('small-date-time', record.startDate);
							}
						},
						{
							key: 'to',
							dataIndex: '',
							title: 'Date to',
							render: (text: any, record: ProductSearchResult) => {
								return this.formatService.toString('small-date-time', record.endDate);
							}
						},
					]
					if (!record.features.isPackage) {
						columns.push({
							key: 'price',
							dataIndex: 'sellPrice',
							title: 'Price',
							render: this.priceRenderer
						});
					}
					
					const selectionKeys = [];
					if (selection != null) {
						for (const prop of Object.getOwnPropertyNames(selection)) {
							const unitIndex = parseInt(prop);
							const id = selection[unitIndex].selected.id;
							if (result.some(p => p.id === id)) {
								selectionKeys.push(`${prop}-${id}`);
							}
						}
					}
					return (
						<React.Fragment>
							<TourInfoCard
								parent={record}
								mode='edit'
								isPackage={record.features.isPackage}
								fromDate={(i: ProductSearchResult) => i.startDate}
								toDate={(i: ProductSearchResult) => i.endDate}
								productName={(i: ProductSearchResult) => i.product.name}
								getSubProcKeys={this.getSubProcKey}
								expandedRenderer={this.expandedRenderer}
								priceRenderer={this.priceRenderer}
								selection={{
									selectedRowKeys: this.selectedProducts[record.id]
										? this.selectedProducts[record.id].map(i => this.getSubProcKey(i))
										: [],
									onChange: this.onSubProductSelect(record),
									getCheckboxProps: this.getCheckProps(record),
									onSelectAll: this.selectAll(record),
								}}
								subProducts={result}
							/>

						</React.Fragment>
					);
				}
			}
		];
	}

	private cancelConfig = () => {
		this.props.onChangeSelection(null, []);
		this.setState({ showTourItineraryConfig: false });
	}

	private priceRenderer = (text: { [unit: number]: Money }, record: ProductSearchResult) => {
		let price: number | null = null;
		let currency: string | null;
		let props = Object.getOwnPropertyNames(toJS(text));
		if (props.length === 0 && record.subProducts.length > 0) {
			text = record.subProducts[0].sellPrice;
			props = Object.getOwnPropertyNames(toJS(text));
		}
		if (text) {
			for (const prop of props) {
				const unit = parseInt(prop);
				if (!!text[unit]) {
					if (price == null) {
						price = text[unit].totalPrice;
						currency = text[unit].currencyIsoCode;
					} else {
						price += text[unit].totalPrice;
					}
				}
			}
		}
		return price ? `${price} ${currency}` : '';
	}
}



// WEBPACK FOOTER //
// ./src/app/bookings/search/results/search-results-tour.view.tsx